/**
 * Admin Route Component
 *
 * Ensures user is authenticated AND has admin privileges.
 * Supports role-based access via allowedRoles prop.
 * Default: any admin role can access. Specify allowedRoles to restrict.
 */

import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin, AdminRole } from "@/hooks/useAdmin";

interface AdminRouteProps {
  children: React.ReactNode;
  allowedRoles?: AdminRole[];
}

export function AdminRoute({ children, allowedRoles }: AdminRouteProps) {
  const { session, loading: authLoading } = useAuth();
  const { isAdmin, role, loading: adminLoading } = useAdmin();

  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Role-based gating: if allowedRoles specified, check user's role
  if (allowedRoles && role && !allowedRoles.includes(role)) {
    // Account managers → redirect to AM portal; Sales → redirect to contracts
    if (role === "account_manager") {
      return <Navigate to="/am" replace />;
    }
    return <Navigate to="/admin/contracts" replace />;
  }

  return <>{children}</>;
}
